import { GoogleGenAI, Modality } from "@google/genai"

const LANGUAGES = [
    { code: "en-US", name: "English" },
    { code: "es-ES", name: "Spanish" },
    { code: "fr-FR", name: "French" },
    { code: "de-DE", name: "German" },
    { code: "it-IT", name: "Italian" },
    { code: "pt-BR", name: "Portuguese (Brazil)" },
    { code: "nl-NL", name: "Dutch" },
    { code: "ja-JP", name: "Japanese" },
    { code: "ko-KR", name: "Korean" },
    { code: "cmn-CN", name: "Mandarin Chinese" },
    { code: "fa-IR", name: "Persian" },
    { code: "ar-XA", name: "Arabic" },
    { code: "hi-IN", name: "Hindi" },
    { code: "ru-RU", name: "Russian" },
    { code: "tr-TR", name: "Turkish" },
    { code: "pl-PL", name: "Polish" },
    { code: "uk-UA", name: "Ukrainian" },
    { code: "vi-VN", name: "Vietnamese" },
    { code: "th-TH", name: "Thai" },
    { code: "id-ID", name: "Indonesian" },
]

const VOICES = [
    { id: "Zephyr", gender: "female", trait: "Bright" },
    { id: "Kore", gender: "female", trait: "Firm" },
    { id: "Orus", gender: "male", trait: "Firm" },
    { id: "Autonoe", gender: "female", trait: "Bright" },
    { id: "Umbriel", gender: "male", trait: "Easy-going" },
    { id: "Erinome", gender: "female", trait: "Clear" },
    { id: "Laomedeia", gender: "female", trait: "Upbeat" },
    { id: "Schedar", gender: "male", trait: "Even" },
    { id: "Achird", gender: "male", trait: "Friendly" },
    { id: "Sadachbia", gender: "female", trait: "Lively" },
    { id: "Puck", gender: "male", trait: "Upbeat" },
    { id: "Fenrir", gender: "male", trait: "Excitable" },
    { id: "Aoede", gender: "female", trait: "Breezy" },
    { id: "Enceladus", gender: "male", trait: "Breathy" },
    { id: "Algieba", gender: "male", trait: "Smooth" },
    { id: "Algenib", gender: "male", trait: "Gravelly" },
    { id: "Achernar", gender: "female", trait: "Soft" },
    { id: "Gacrux", gender: "male", trait: "Mature" },
    { id: "Zubenelgenubi", gender: "male", trait: "Casual" },
    { id: "Sadaltager", gender: "male", trait: "Knowledgeable" },
    { id: "Charon", gender: "male", trait: "Informative" },
    { id: "Leda", gender: "female", trait: "Youthful" },
    { id: "Callirrhoe", gender: "female", trait: "Easy-going" },
    { id: "Iapetus", gender: "male", trait: "Clear" },
    { id: "Despina", gender: "female", trait: "Smooth" },
    { id: "Rasalgethi", gender: "male", trait: "Informative" },
    { id: "Alnilam", gender: "male", trait: "Firm" },
    { id: "Pulcherrima", gender: "female", trait: "Forward" },
    { id: "Vindemiatrix", gender: "female", trait: "Gentle" },
    { id: "Sulafat", gender: "female", trait: "Warm" },
]

const PLAYBACK_SAMPLE_RATE = 24000
const INPUT_SAMPLE_RATE = 16000

const STORAGE_KEY = "syncspeak-prefs"

function systemInstruction(source, target, sourceAccent, targetAccent) {
    let instruction =
        `You are a highly skilled real-time, simultaneous interpreter. Your input audio will strictly be in ${source}.`
    const srcAccent = sourceAccent?.trim()
    const tgtAccent = targetAccent?.trim()
    if (srcAccent) {
        instruction += ` The speaker uses a ${srcAccent} accent when speaking ${source}.`
    }
    instruction += ` You must translate everything spoken into natural, conversational ${target}.`
    if (tgtAccent) {
        instruction += ` Speak the translation with a ${tgtAccent} accent.`
    }
    instruction +=
        ` Do not add commentary, do not answer questions, and do not converse with the user. Only output the direct ${target} translation.`
    return instruction
}

function loadPrefs() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) ?? {}
    } catch {
        return {}
    }
}

function savePrefs(prefs) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
}

function getStoredDevices() {
    const devices = loadPrefs().devices ?? {}
    return {
        a: { input: devices.a?.input ?? "", output: devices.a?.output ?? "" },
        b: { input: devices.b?.input ?? "", output: devices.b?.output ?? "" },
    }
}

function saveStoredDevices() {
    const prefs = loadPrefs()
    prefs.devices = {
        a: { input: sides.a.inputSelect.value, output: sides.a.outputSelect.value },
        b: { input: sides.b.inputSelect.value, output: sides.b.outputSelect.value },
    }
    savePrefs(prefs)
}

function pickDevice(select, deviceId) {
    if (!deviceId) return false
    if ([...select.options].some(o => o.value === deviceId)) {
        select.value = deviceId
        return true
    }
    return false
}

// ------------------------------------------------------------------
// State
// ------------------------------------------------------------------

const sides = {
    a: createSideState("a"),
    b: createSideState("b"),
}

let permissionGranted = false
let tokenCache = null

function createSideState(id) {
    const root = document.querySelector(`.side[data-side='${id}']`)
    return {
        id,
        root,
        inputSelect: root.querySelector("select[data-role='input']"),
        outputSelect: root.querySelector("select[data-role='output']"),
        langSelect: root.querySelector("select[data-role='lang']"),
        accentInput: root.querySelector("input[data-role='accent']"),
        voiceSelect: root.querySelector("select[data-role='voice']"),
        micBtn: root.querySelector("button[data-role='mic']"),
        statusEl: root.querySelector("[data-role='status']"),
        sinkEl: root.querySelector("audio[data-role='sink']"),
        transcriptTranslationLog: root.querySelector("[data-role='transcript-translation']"),
        transcriptTranslationLive: root.querySelector("[data-role='transcript-translation-live']"),
        transcriptOriginalLog: root.querySelector("[data-role='transcript-original']"),
        transcriptOriginalLive: root.querySelector("[data-role='transcript-original-live']"),
        routingEl: root.querySelector("[data-role='routing']"),
        transcriptBuffers: {
            translation: "",
            original: "",
        },
        session: null,
        sessionLangs: null,
        sessionOpening: null,
        sessionAlive: false,
        streaming: false,
        micActive: false,
        micCtx: null,
        micStream: null,
        micWorklet: null,
        micSource: null,
        playback: null,
    }
}

function other(id) {
    return id === "a" ? "b" : "a"
}

function listenerFor(speaker) {
    return sides[other(speaker.id)]
}

function personLabel(id) {
    return id === "a" ? "Person A" : "Person B"
}

// ------------------------------------------------------------------
// UI helpers
// ------------------------------------------------------------------

const globalStatusEl = document.getElementById("globalStatus")
const grantBtn = document.getElementById("grantBtn")

function setGlobalStatus(text, tone) {
    globalStatusEl.textContent = text
    globalStatusEl.className = ""
    if (tone) globalStatusEl.classList.add(`status-${tone}`)
}

function setSideStatus(side, text, tone) {
    side.statusEl.textContent = text
    side.statusEl.className = "side-status"
    if (tone) side.statusEl.classList.add(tone)
}

function updateRoutingHints() {
    for (const id of ["a", "b"]) {
        const side = sides[id]
        const listener = sides[other(id)]
        const outLabel = listener.outputSelect.selectedOptions[0]?.textContent ?? "other speaker"
        side.routingEl.textContent =
            `Translations for your speech play on ${personLabel(listener.id)}'s speaker (${outLabel}).`
    }
}

function warnIfSameOutputDevice() {
    const aOut = sides.a.outputSelect.value
    const bOut = sides.b.outputSelect.value
    if (!aOut || !bOut || aOut !== bOut) return false
    setGlobalStatus(
        "Both sides use the same speaker device — you will hear translations on your own headphones. Pick different outputs for A and B.",
        "warn",
    )
    return true
}

function fillLanguageOptions(select, defaultCode) {
    select.innerHTML = ""
    for (const lang of LANGUAGES) {
        const opt = document.createElement("option")
        opt.value = lang.code
        opt.textContent = lang.name
        if (lang.code === defaultCode) opt.selected = true
        select.appendChild(opt)
    }
}

function fillVoiceOptions(select, defaultId) {
    select.innerHTML = ""
    for (const voice of VOICES) {
        const opt = document.createElement("option")
        opt.value = voice.id
        opt.textContent = `${voice.id} (${voice.gender}) — ${voice.trait}`
        if (voice.id === defaultId) opt.selected = true
        select.appendChild(opt)
    }
}

function fillDeviceOptions(select, devices, preserveValue) {
    const prev = preserveValue ?? select.value
    select.innerHTML = ""
    if (devices.length === 0) {
        const opt = document.createElement("option")
        opt.value = ""
        opt.textContent = "(no devices found)"
        select.appendChild(opt)
        return
    }
    for (const d of devices) {
        const opt = document.createElement("option")
        opt.value = d.deviceId
        opt.textContent = d.label || `Device ${d.deviceId.slice(0, 8)}`
        select.appendChild(opt)
    }
    if (prev && [...select.options].some(o => o.value === prev)) {
        select.value = prev
    }
}

// ------------------------------------------------------------------
// Device enumeration
// ------------------------------------------------------------------

async function refreshDevices() {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const inputs = devices.filter(d => d.kind === "audioinput")
    const outputs = devices.filter(d => d.kind === "audiooutput")
    const stored = getStoredDevices()

    fillDeviceOptions(sides.a.inputSelect, inputs, stored.a.input || sides.a.inputSelect.value)
    fillDeviceOptions(sides.b.inputSelect, inputs, stored.b.input || sides.b.inputSelect.value)
    fillDeviceOptions(sides.a.outputSelect, outputs, stored.a.output || sides.a.outputSelect.value)
    fillDeviceOptions(sides.b.outputSelect, outputs, stored.b.output || sides.b.outputSelect.value)

    pickDevice(sides.a.inputSelect, stored.a.input)
    pickDevice(sides.b.inputSelect, stored.b.input)
    pickDevice(sides.a.outputSelect, stored.a.output)
    pickDevice(sides.b.outputSelect, stored.b.output)

    if (outputs.length >= 2 && sides.a.outputSelect.value === sides.b.outputSelect.value) {
        const alt = outputs.find(d => d.deviceId !== sides.a.outputSelect.value)
        if (alt && !stored.b.output) sides.b.outputSelect.value = alt.deviceId
    }

    saveStoredDevices()
    updateRoutingHints()
    warnIfSameOutputDevice()
}

async function grantPermission() {
    if (!navigator.mediaDevices?.getUserMedia) {
        setGlobalStatus("Microphone API not available in this browser", "bad")
        return false
    }
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        for (const t of stream.getTracks()) t.stop()
        permissionGranted = true
        grantBtn.disabled = true
        grantBtn.textContent = "Microphone access granted"
        setGlobalStatus("Ready", "ok")
        await refreshDevices()
        sides.a.micBtn.disabled = false
        sides.b.micBtn.disabled = false
        return true
    } catch (err) {
        permissionGranted = false
        grantBtn.disabled = false
        grantBtn.textContent = "Grant microphone access"
        setGlobalStatus(`Permission denied — click the button to try again (${err.message})`, "warn")
        return false
    }
}

async function tryAutoGrantPermission() {
    if (!navigator.mediaDevices?.getUserMedia) {
        setGlobalStatus("Click 'Grant microphone access' to begin", "warn")
        return
    }
    setGlobalStatus("Requesting microphone access...", "warn")
    await grantPermission()
}

// ------------------------------------------------------------------
// Token + Gemini client
// ------------------------------------------------------------------

async function getToken() {
    if (tokenCache && tokenCache.expiresAt > Date.now() + 30_000) {
        return tokenCache
    }
    const res = await fetch("/api/token")
    if (!res.ok) {
        const body = await res.text()
        throw new Error(`Token fetch failed (${res.status}): ${body}`)
    }
    const data = await res.json()
    tokenCache = {
        name: data.token,
        model: data.model,
        expiresAt: Date.now() + 9 * 60_000,
    }
    return tokenCache
}

async function openSession(side) {
    if (side.session) return side.session
    if (side.sessionOpening) return side.sessionOpening

    const listener = listenerFor(side)
    const sourceCode = side.langSelect.value
    const targetCode = listener.langSelect.value
    const voiceName = listener.voiceSelect.value
    const source = LANGUAGES.find(l => l.code === sourceCode)?.name ?? sourceCode
    const target = LANGUAGES.find(l => l.code === targetCode)?.name ?? targetCode
    const sourceAccent = side.accentInput.value
    const targetAccent = listener.accentInput.value
    side.sessionLangs = { sourceCode, targetCode, voiceName, sourceAccent, targetAccent }

    side.sessionOpening = (async () => {
        const token = await getToken()
        const client = new GoogleGenAI({
            apiKey: token.name,
            httpOptions: { apiVersion: "v1alpha" },
        })

        const listenerSide = listenerFor(side)

        const session = await client.live.connect({
            model: token.model,
            config: {
                responseModalities: [Modality.AUDIO],
                systemInstruction: systemInstruction(source, target, sourceAccent, targetAccent),
                speechConfig: {
                    voiceConfig: {
                        prebuiltVoiceConfig: { voiceName },
                    },
                },
                inputAudioTranscription: {},
                outputAudioTranscription: {},
            },
            callbacks: {
                onopen: () => {
                    side.sessionAlive = true
                    setSideStatus(side, `Connected (${source} -> ${target})`, "ok")
                },
                onmessage: (msg) => handleServerMessage(side, listenerSide, msg),
                onerror: (e) => {
                    console.error(`Session ${side.id} error:`, e)
                    setSideStatus(side, `Session error: ${e.message ?? "unknown"}`, "bad")
                },
                onclose: (e) => {
                    console.log(`Session ${side.id} closed:`, {
                        code: e.code,
                        reason: e.reason,
                        wasClean: e.wasClean,
                    })
                    side.sessionAlive = false
                    side.streaming = false
                    side.session = null
                    side.sessionLangs = null
                    if (side.micActive) {
                        setSideStatus(side, `Session closed: ${e.reason || "unknown"}`, "bad")
                        stopMic(side)
                    }
                },
            },
        })

        side.session = session
        side.sessionOpening = null
        return session
    })()

    try {
        return await side.sessionOpening
    } catch (err) {
        side.sessionOpening = null
        side.session = null
        setSideStatus(side, `Failed to open session: ${err.message}`, "bad")
        throw err
    }
}

function closeSession(side) {
    side.streaming = false
    side.sessionAlive = false
    if (side.session) {
        try { side.session.close() } catch {}
    }
    side.session = null
    side.sessionLangs = null
    side.sessionOpening = null
}

function invalidateSessionsAfterConfigChange() {
    for (const id of ["a", "b"]) {
        const speaker = sides[id]
        const listener = sides[other(id)]
        const langs = speaker.sessionLangs
        if (!langs) continue
        const desiredSrc = speaker.langSelect.value
        const desiredTgt = listener.langSelect.value
        const desiredVoice = listener.voiceSelect.value
        const desiredSrcAccent = speaker.accentInput.value
        const desiredTgtAccent = listener.accentInput.value
        if (
            langs.sourceCode !== desiredSrc
            || langs.targetCode !== desiredTgt
            || langs.voiceName !== desiredVoice
            || langs.sourceAccent !== desiredSrcAccent
            || langs.targetAccent !== desiredTgtAccent
        ) {
            closeSession(speaker)
            if (speaker.micActive) {
                stopMic(speaker)
                startMic(speaker).catch(err => {
                    console.error(err)
                    setSideStatus(speaker, err.message, "bad")
                })
            }
        }
    }
}

function appendTranscript(side, role, text, finished) {
    if (text) side.transcriptBuffers[role] += text
    const logEl = role === "translation"
        ? side.transcriptTranslationLog
        : side.transcriptOriginalLog
    const liveEl = role === "translation"
        ? side.transcriptTranslationLive
        : side.transcriptOriginalLive

    liveEl.textContent = side.transcriptBuffers[role]

    if (finished && side.transcriptBuffers[role]) {
        const p = document.createElement("p")
        p.textContent = side.transcriptBuffers[role]
        logEl.insertBefore(p, liveEl)
        side.transcriptBuffers[role] = ""
        liveEl.textContent = ""
        logEl.scrollTop = logEl.scrollHeight
    }
}

function flushTranscript(side, role) {
    const buf = side.transcriptBuffers[role]
    if (!buf) return
    const logEl = role === "translation"
        ? side.transcriptTranslationLog
        : side.transcriptOriginalLog
    const liveEl = role === "translation"
        ? side.transcriptTranslationLive
        : side.transcriptOriginalLive
    const p = document.createElement("p")
    p.textContent = buf
    logEl.insertBefore(p, liveEl)
    side.transcriptBuffers[role] = ""
    liveEl.textContent = ""
    logEl.scrollTop = logEl.scrollHeight
}

// ------------------------------------------------------------------
// Inbound audio playback
// ------------------------------------------------------------------

async function ensurePlayback(listener) {
    if (listener.playback) {
        await applyOutputSink(listener)
        return listener.playback
    }
    const ctx = new AudioContext({ sampleRate: PLAYBACK_SAMPLE_RATE })
    const dest = ctx.createMediaStreamDestination()
    listener.sinkEl.srcObject = dest.stream
    listener.playback = { ctx, dest, nextStartTime: 0 }
    await applyOutputSink(listener)
    await listener.sinkEl.play().catch(() => {})
    return listener.playback
}

async function applyOutputSink(side) {
    const deviceId = side.outputSelect.value
    if (!deviceId) return
    if (typeof side.sinkEl.setSinkId === "function") {
        await side.sinkEl.setSinkId(deviceId)
    }
}

function base64ToInt16(b64) {
    const binary = atob(b64)
    const len = binary.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)
    return new Int16Array(bytes.buffer, bytes.byteOffset, len / 2)
}

function int16ToFloat32(i16) {
    const f32 = new Float32Array(i16.length)
    for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 0x8000
    return f32
}

async function playPcm(listener, b64) {
    const playback = await ensurePlayback(listener)
    const { ctx, dest } = playback
    const i16 = base64ToInt16(b64)
    if (i16.length === 0) return
    const f32 = int16ToFloat32(i16)
    const buffer = ctx.createBuffer(1, f32.length, PLAYBACK_SAMPLE_RATE)
    buffer.copyToChannel(f32, 0)
    const src = ctx.createBufferSource()
    src.buffer = buffer
    src.connect(dest)
    const now = ctx.currentTime
    const startAt = Math.max(now, playback.nextStartTime)
    src.start(startAt)
    playback.nextStartTime = startAt + buffer.duration
}

function stopPlayback(sinkSide, hardReset) {
    if (!sinkSide.playback) return
    if (hardReset) {
        sinkSide.playback.nextStartTime = 0
    }
}

function handleServerMessage(speaker, listener, msg) {
    if (msg.setupComplete) {
        console.log(`Session ${speaker.id} setup complete → audio to ${listener.id}`)
    }
    if (msg.goAway) {
        console.warn(`Session ${speaker.id} goAway:`, msg.goAway)
        setSideStatus(speaker, `Server requested disconnect (${msg.goAway.timeLeft ?? ""})`, "warn")
    }
    const sc = msg.serverContent
    if (!sc) return

    if (sc.outputTranscription?.text || sc.outputTranscription?.finished) {
        appendTranscript(
            listener,
            "translation",
            sc.outputTranscription.text ?? "",
            sc.outputTranscription.finished ?? false,
        )
    }
    if (sc.inputTranscription?.text || sc.inputTranscription?.finished) {
        appendTranscript(
            listener,
            "original",
            sc.inputTranscription.text ?? "",
            sc.inputTranscription.finished ?? false,
        )
    }

    const parts = sc.modelTurn?.parts ?? []
    for (const part of parts) {
        const inline = part.inlineData
        if (inline?.data && inline.mimeType?.startsWith("audio/")) {
            playPcm(listener, inline.data).catch(err => {
                console.error(`Playback to ${listener.id} failed:`, err)
            })
        }
    }
    if (sc.interrupted) {
        stopPlayback(listener, true)
        flushTranscript(listener, "translation")
        flushTranscript(listener, "original")
    }
}

// ------------------------------------------------------------------
// Mic pipeline
// ------------------------------------------------------------------

function int16ArrayToBase64(buf) {
    const bytes = new Uint8Array(buf)
    let binary = ""
    const chunk = 0x8000
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk))
    }
    return btoa(binary)
}

async function startMic(speaker) {
    try {
        const listener = listenerFor(speaker)
        setSideStatus(speaker, "Connecting...", "warn")
        warnIfSameOutputDevice()
        await openSession(speaker)
        await ensurePlayback(listener)

        const deviceId = speaker.inputSelect.value
        const constraints = {
            audio: {
                deviceId: deviceId ? { exact: deviceId } : undefined,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                channelCount: 1,
            },
        }
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        const ctx = new AudioContext()
        await ctx.audioWorklet.addModule("/pcm-worklet.js")
        const source = ctx.createMediaStreamSource(stream)
        const worklet = new AudioWorkletNode(ctx, "pcm-worklet")

        worklet.port.onmessage = (e) => {
            if (!speaker.streaming || !speaker.sessionAlive || !speaker.session) return
            const b64 = int16ArrayToBase64(e.data)
            try {
                speaker.session.sendRealtimeInput({
                    audio: { data: b64, mimeType: `audio/pcm;rate=${INPUT_SAMPLE_RATE}` },
                })
            } catch (err) {
                console.error("sendRealtimeInput failed:", err)
                speaker.streaming = false
            }
        }

        source.connect(worklet)

        speaker.micStream = stream
        speaker.micCtx = ctx
        speaker.micWorklet = worklet
        speaker.micSource = source
        speaker.micActive = true
        speaker.streaming = true
        speaker.micBtn.classList.add("active")
        speaker.micBtn.querySelector(".mic-label").textContent = "Stop mic"
        const listenerOut = listener.outputSelect.selectedOptions[0]?.textContent ?? "other speaker"
        setSideStatus(
            speaker,
            `Listening → ${personLabel(listener.id)} (${listenerOut})`,
            "ok",
        )
        updateRoutingHints()
    } catch (err) {
        console.error(err)
        setSideStatus(speaker, `Mic error: ${err.message}`, "bad")
        stopMic(speaker)
        throw err
    }
}

function stopMic(side) {
    side.streaming = false
    if (side.micWorklet) {
        try { side.micWorklet.port.onmessage = null } catch {}
        try { side.micWorklet.disconnect() } catch {}
    }
    if (side.micSource) {
        try { side.micSource.disconnect() } catch {}
    }
    if (side.micStream) {
        for (const t of side.micStream.getTracks()) t.stop()
    }
    if (side.micCtx) {
        side.micCtx.close().catch(() => {})
    }
    side.micWorklet = null
    side.micSource = null
    side.micStream = null
    side.micCtx = null
    side.micActive = false
    side.micBtn.classList.remove("active")
    side.micBtn.querySelector(".mic-label").textContent = "Start mic"
    if (!side.statusEl.classList.contains("bad")) {
        setSideStatus(side, "Idle")
    }
}

// ------------------------------------------------------------------
// Wire-up
// ------------------------------------------------------------------

function init() {
    fillLanguageOptions(sides.a.langSelect, "en-US")
    fillLanguageOptions(sides.b.langSelect, "es-ES")
    fillVoiceOptions(sides.a.voiceSelect, "Kore")
    fillVoiceOptions(sides.b.voiceSelect, "Charon")

    fillDeviceOptions(sides.a.inputSelect, [])
    fillDeviceOptions(sides.a.outputSelect, [])
    fillDeviceOptions(sides.b.inputSelect, [])
    fillDeviceOptions(sides.b.outputSelect, [])

    updateRoutingHints()

    grantBtn.addEventListener("click", () => {
        grantPermission()
    })

    for (const id of ["a", "b"]) {
        const side = sides[id]

        side.micBtn.addEventListener("click", async () => {
            if (!permissionGranted) {
                await grantPermission()
                if (!permissionGranted) return
            }
            if (side.micActive) {
                stopMic(side)
            } else {
                try {
                    await startMic(side)
                } catch (err) {
                    console.error(err)
                }
            }
        })

        side.langSelect.addEventListener("change", () => {
            invalidateSessionsAfterConfigChange()
        })

        side.accentInput.addEventListener("change", () => {
            invalidateSessionsAfterConfigChange()
        })
        side.accentInput.addEventListener("keydown", (e) => {
            if (e.key === "Enter") invalidateSessionsAfterConfigChange()
        })

        side.voiceSelect.addEventListener("change", () => {
            invalidateSessionsAfterConfigChange()
        })

        side.inputSelect.addEventListener("change", async () => {
            saveStoredDevices()
            if (side.micActive) {
                stopMic(side)
                try {
                    await startMic(side)
                } catch (err) {
                    console.error(err)
                }
            }
        })

        side.outputSelect.addEventListener("change", async () => {
            saveStoredDevices()
            updateRoutingHints()
            warnIfSameOutputDevice()
            if (side.playback) {
                try {
                    await applyOutputSink(side)
                } catch (err) {
                    console.warn("setSinkId failed:", err)
                    setSideStatus(side, `Output device error: ${err.message}`, "bad")
                }
            }
        })
    }

    navigator.mediaDevices?.addEventListener("devicechange", () => {
        if (permissionGranted) {
            refreshDevices().catch(err => console.warn("Device refresh failed:", err))
        }
    })

    tryAutoGrantPermission()
}

init()
